import { translate as t } from '@thoughtspot/i18n';
import { getCurrentOrgId, getSessionInfo, isOrgUiEnabled, setSessionInfo, } from '@thoughtspot/session-service';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useGetPinboardListQuery } from "../../../services/pinboard-list/pinboard-list-service";
import { getConfigInfoOverrides, updateConfigInfo, } from "../../../services/system/system-service";
import { LiveboardCacheConfig } from './liveboard-cache-config';
const idsToSelectedLiveboards = (ids, nameMap) => ids.map(id => ({ id, name: nameMap.get(id) || id }));
export const LiveboardCacheConfigContainer = ({ orgViewModeType, onEditingChange, onShowToast, }) => {
    const isOrgEnabled = isOrgUiEnabled();
    const isAllOrgContext = isOrgEnabled && orgViewModeType === 'all-org';
    const canSelectLiveboards = !isAllOrgContext;
    const currentOrgKey = getCurrentOrgId().toString();
    const isMountedRef = useRef(true);
    const tscliEnabled = !!getSessionInfo()?.configInfo
        ?.liveboardVizDataCacheEnabled;
    const [clusterOrgEnabled, setClusterOrgEnabled] = useState(false);
    const perOrgAllowAllRef = useRef({});
    const perOrgLiveboardIdsRef = useRef({});
    const [pendingClusterOrgEnabled, setPendingClusterOrgEnabled] = useState(false);
    const [pendingAllowAll, setPendingAllowAll] = useState(false);
    const [selectedLiveboards, setSelectedLiveboards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [selectorSearchPattern, setSelectorSearchPattern] = useState('');
    const [debouncedPattern, setDebouncedPattern] = useState('');
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedPattern(selectorSearchPattern), 300);
        return () => clearTimeout(timer);
    }, [selectorSearchPattern]);
    const skipLiveboardList = !canSelectLiveboards || !isEditing || pendingAllowAll;
    const { data: pinboardListData, loading: pinboardListLoading, } = useGetPinboardListQuery({ batchSize: 1000, offset: 0, pattern: debouncedPattern || undefined }, skipLiveboardList);
    const liveboardNameMap = useMemo(() => {
        const map = new Map();
        pinboardListData?.getPinboardList?.objects?.forEach(lb => {
            if (lb?.id && lb?.name) {
                map.set(lb.id, lb.name);
            }
        });
        return map;
    }, [pinboardListData]);
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);
    const fetchConfig = async () => {
        try {
            setLoading(true);
            const overrides = await getConfigInfoOverrides();
            const orgEnabled = !!overrides.liveboardVizDataCacheOrgEnabled;
            const perOrgAllowAll = (overrides.liveboardVizDataCacheAllLiveboardsPerOrg ??
                {});
            const perOrgLiveboardIds = (overrides.liveboardVizDataCacheLiveboardIdsPerOrg ??
                {});
            if (!isMountedRef.current)
                return;
            setClusterOrgEnabled(orgEnabled);
            perOrgAllowAllRef.current = {
                ...perOrgAllowAllRef.current,
                ...perOrgAllowAll,
            };
            perOrgLiveboardIdsRef.current = {
                ...perOrgLiveboardIdsRef.current,
                ...perOrgLiveboardIds,
            };
            if (isAllOrgContext) {
                setPendingClusterOrgEnabled(orgEnabled);
                setSelectedLiveboards([]);
            }
            else {
                const orgAllowAll = perOrgAllowAllRef.current[currentOrgKey] ?? false;
                const orgIds = perOrgLiveboardIdsRef.current[currentOrgKey] ?? [];
                setPendingAllowAll(orgAllowAll);
                setSelectedLiveboards(orgIds.map(id => ({ id, name: id })));
            }
            setIsEditing(false);
            onEditingChange?.(false);
        }
        catch {
        }
        finally {
            if (isMountedRef.current) {
                setLoading(false);
            }
        }
    };
    useEffect(() => {
        fetchConfig();
    }, [orgViewModeType, isOrgEnabled]);
    useEffect(() => {
        if (isEditing && liveboardNameMap.size > 0) {
            setSelectedLiveboards(prev => prev.map(lb => ({
                ...lb,
                name: liveboardNameMap.get(lb.id) || lb.name || lb.id,
            })));
        }
    }, [liveboardNameMap, isEditing]);
    const handleSave = async () => {
        try {
            setSaving(true);
            let changes = {};
            if (isAllOrgContext) {
                changes = {
                    liveboardVizDataCacheOrgEnabled: pendingClusterOrgEnabled,
                };
            }
            else {
                const liveboardIds = selectedLiveboards.map(lb => lb.id);
                changes = {
                    liveboardVizDataCacheAllLiveboardsPerOrg: {
                        [currentOrgKey]: pendingAllowAll,
                    },
                    liveboardVizDataCacheLiveboardIdsPerOrg: {
                        [currentOrgKey]: liveboardIds,
                    },
                };
                perOrgAllowAllRef.current = {
                    ...perOrgAllowAllRef.current,
                    [currentOrgKey]: pendingAllowAll,
                };
                perOrgLiveboardIdsRef.current = {
                    ...perOrgLiveboardIdsRef.current,
                    [currentOrgKey]: liveboardIds,
                };
            }
            await updateConfigInfo(changes);
            if (!isMountedRef.current)
                return;
            if (isAllOrgContext) {
                setClusterOrgEnabled(pendingClusterOrgEnabled);
            }
            const existingSessionInfo = getSessionInfo();
            if (existingSessionInfo) {
                setSessionInfo({
                    ...existingSessionInfo,
                    configInfo: {
                        ...existingSessionInfo.configInfo,
                        ...(isAllOrgContext
                            ? {
                                liveboardVizDataCacheOrgEnabled: pendingClusterOrgEnabled,
                            }
                            : {
                                liveboardVizDataCacheAllLiveboardsPerOrg: perOrgAllowAllRef.current,
                                liveboardVizDataCacheLiveboardIdsPerOrg: perOrgLiveboardIdsRef.current,
                            }),
                    },
                });
            }
            setIsEditing(false);
            onEditingChange?.(false);
            onShowToast?.(t('tscli.ui.appsettings.datasave.success.message'));
        }
        catch {
        }
        finally {
            if (isMountedRef.current) {
                setSaving(false);
            }
        }
    };
    const startEditing = () => {
        if (saving)
            return;
        if (isAllOrgContext) {
            setPendingClusterOrgEnabled(clusterOrgEnabled);
        }
        else {
            const orgAllowAll = perOrgAllowAllRef.current[currentOrgKey] ?? false;
            const orgIds = perOrgLiveboardIdsRef.current[currentOrgKey] ?? [];
            setPendingAllowAll(orgAllowAll);
            setSelectedLiveboards(idsToSelectedLiveboards(orgIds, liveboardNameMap));
        }
        setSelectorSearchPattern('');
        setDebouncedPattern('');
        setIsEditing(true);
        onEditingChange?.(true);
    };
    const cancelEditing = () => {
        if (isAllOrgContext) {
            setPendingClusterOrgEnabled(clusterOrgEnabled);
        }
        else {
            const orgAllowAll = perOrgAllowAllRef.current[currentOrgKey] ?? false;
            const orgIds = perOrgLiveboardIdsRef.current[currentOrgKey] ?? [];
            setPendingAllowAll(orgAllowAll);
            setSelectedLiveboards(idsToSelectedLiveboards(orgIds, liveboardNameMap));
        }
        setSelectorSearchPattern('');
        setDebouncedPattern('');
        setIsEditing(false);
        onEditingChange?.(false);
    };
    const handleOrgEnabledChange = (value) => {
        setPendingClusterOrgEnabled(value);
    };
    const handleAllowAllChange = (allowAll) => {
        setPendingAllowAll(allowAll);
        if (allowAll) {
            setSelectedLiveboards([]);
        }
    };
    const handleAddLiveboard = (liveboard) => {
        if (!selectedLiveboards.some(lb => lb.id === liveboard.id)) {
            setSelectedLiveboards([...selectedLiveboards, liveboard]);
        }
    };
    const handleRemoveLiveboard = (id) => {
        setSelectedLiveboards(selectedLiveboards.filter(lb => lb.id !== id));
    };
    const handleSelectorSearch = (pattern) => {
        setSelectorSearchPattern(pattern);
    };
    const availableLiveboards = useMemo(() => {
        const objects = pinboardListData?.getPinboardList?.objects || [];
        return objects
            .filter((lb) => !selectedLiveboards.some(s => s.id === lb.id))
            .map((lb) => ({
            id: lb.id,
            name: lb.name || lb.id,
        }));
    }, [pinboardListData, selectedLiveboards]);
    const storedOrgAllowAll = perOrgAllowAllRef.current[currentOrgKey] ?? false;
    const storedOrgLiveboardIds = perOrgLiveboardIdsRef.current[currentOrgKey] ?? [];
    const hasChanges = useMemo(() => {
        if (isAllOrgContext) {
            return pendingClusterOrgEnabled !== clusterOrgEnabled;
        }
        const selectedIds = JSON.stringify(selectedLiveboards.map(lb => lb.id).sort());
        const storedIds = JSON.stringify([...storedOrgLiveboardIds].sort());
        return (pendingAllowAll !== storedOrgAllowAll || selectedIds !== storedIds);
    }, [
        isAllOrgContext,
        pendingClusterOrgEnabled,
        pendingAllowAll,
        clusterOrgEnabled,
        storedOrgAllowAll,
        selectedLiveboards,
        storedOrgLiveboardIds,
    ]);
    if (loading || !tscliEnabled) {
        return null;
    }
    return (React.createElement(LiveboardCacheConfig, { isAllOrgContext: isAllOrgContext, isOrgEnabled: isOrgEnabled, clusterOrgEnabled: clusterOrgEnabled, allowAll: storedOrgAllowAll, liveboardIds: storedOrgLiveboardIds, pendingClusterOrgEnabled: pendingClusterOrgEnabled, pendingAllowAll: pendingAllowAll, selectedLiveboards: selectedLiveboards, hasChanges: hasChanges, onOrgEnabledChange: handleOrgEnabledChange, onAllowAllChange: handleAllowAllChange, onAddLiveboard: handleAddLiveboard, onRemoveLiveboard: handleRemoveLiveboard, onSave: handleSave, onEdit: startEditing, onCancel: cancelEditing, isEditing: isEditing, saving: saving, availableLiveboards: availableLiveboards, selectorLoading: pinboardListLoading, onSelectorSearch: handleSelectorSearch }));
};
//# sourceMappingURL=liveboard-cache-config.container.js.map